/**
 * Select — the PulsePanels dropdown (StyledSelect) with the native <select> API, so a page swaps
 * `<select>` for `<Select>` and keeps its `<option>` children and `onChange={(e) => …e.target.value}`.
 * The option list is drawn by us (native popups can't be themed), portaled to <body> and placed
 * under the trigger. Keyboard: ↑/↓ open and move, Enter picks, Esc closes; long lists get a search box.
 */
import React, { isValidElement, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Opt {
  value: string;
  label: ReactNode;
  text: string;
  disabled: boolean;
}

function textOf(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

function collect(children: ReactNode, out: Opt[]) {
  React.Children.forEach(children, (c) => {
    if (!isValidElement<{ value?: string | number; children?: ReactNode; disabled?: boolean }>(c)) return;
    if (c.type === 'option') {
      const label = c.props.children;
      const value = c.props.value !== undefined ? String(c.props.value) : textOf(label);
      out.push({ value, label, text: textOf(label).toLowerCase(), disabled: !!c.props.disabled });
    } else collect(c.props.children, out);
  });
}

export function Select({
  value,
  onChange,
  children,
  disabled,
  className = '',
  style,
  size = 'md',
  title,
  id,
  'aria-label': ariaLabel,
}: {
  value: string | number | undefined | null;
  onChange?: (e: { target: { value: string } }) => void;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  size?: 'md' | 'sm';
  title?: string;
  id?: string;
  required?: boolean;
  'aria-label'?: string;
}) {
  const options = useMemo(() => {
    const out: Opt[] = [];
    collect(children, out);
    return out;
  }, [children]);
  const current = value == null ? '' : String(value);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<CSSProperties>({ visibility: 'hidden' });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchable = options.length > 10;
  const selected = options.find((o) => o.value === current);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.text.includes(s)) : options;
  }, [options, q]);

  const close = (focus = false) => {
    setOpen(false);
    setQ('');
    if (focus) btnRef.current?.focus();
  };
  const pick = (o: Opt) => {
    if (o.disabled) return;
    if (o.value !== current) onChange?.({ target: { value: o.value } });
    close(true);
  };

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const estH = Math.min(304, options.length * 38 + 12) + (searchable ? 44 : 0);
    const up = r.bottom + 4 + estH > window.innerHeight - 8 && r.top - estH > 8;
    const minW = Math.max(r.width, 168);
    setPos({ position: 'fixed', top: up ? r.top - estH - 4 : r.bottom + 4, left: Math.min(r.left, window.innerWidth - minW - 8), minWidth: minW, visibility: 'visible' });
    setActive(Math.max(0, options.findIndex((o) => o.value === current)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const pop = popRef.current;
    const el = open && active >= 0 ? pop?.querySelectorAll<HTMLElement>('.pp-select-opt')[active] : null;
    if (!pop || !el) return;
    if (el.offsetTop < pop.scrollTop + 40) pop.scrollTop = Math.max(0, el.offsetTop - 44);
    else if (el.offsetTop + el.offsetHeight > pop.scrollTop + pop.clientHeight) pop.scrollTop = el.offsetTop + el.offsetHeight - pop.clientHeight + 6;
  }, [open, active]);

  useEffect(() => setActive(shown.length ? 0 : -1), [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && searchable && pos.visibility === 'visible') searchRef.current?.focus({ preventScroll: true });
  }, [open, searchable, pos.visibility]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(true);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const d = e.key === 'ArrowDown' ? 1 : -1;
        setActive((a) => (shown.length ? (a + d + shown.length) % shown.length : -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (shown[active]) pick(shown[active]);
      } else if (e.key === 'Tab') close();
    };
    const onScroll = (e: Event) => {
      if (popRef.current && e.target instanceof Node && popRef.current.contains(e.target)) return;
      close();
    };
    const onResize = () => close();
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, shown, active]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <button
        ref={btnRef}
        id={id}
        type="button"
        title={title}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`pp-select pp-select-${size}${open ? ' is-open' : ''}${className ? ' ' + className : ''}`}
        style={style}
        onClick={() => (open ? close() : !disabled && setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            if (!disabled) setOpen(true);
          }
        }}
      >
        <span className="pp-select-val">{selected ? selected.label : ' '}</span>
        <svg className="pp-select-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open &&
        !disabled &&
        createPortal(
          <>
            <div className="pp-select-backdrop" onMouseDown={() => close()} />
            <div ref={popRef} className="pp-select-pop" style={pos} role="listbox" aria-label={ariaLabel} onMouseDown={(e) => e.stopPropagation()}>
              {searchable && <input ref={searchRef} className="pp-select-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" aria-label="Search" />}
              {shown.map((o, i) => (
                <button
                  key={o.value + i}
                  type="button"
                  role="option"
                  aria-selected={o.value === current}
                  disabled={o.disabled}
                  className={`pp-select-opt${o.value === current ? ' is-sel' : ''}${i === active ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o)}
                >
                  <span className="pp-select-opt-label">{o.label}</span>
                  {o.value === current && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                  )}
                </button>
              ))}
              {!shown.length && <div className="pp-select-empty">No match</div>}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
